import { purchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { mutatePurchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormSelect } from "@/features/standard/Form/FormSelect";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import customAlert from "@/features/ui/alert/alert";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { PurchaseOrderAutoComplete } from "@/features/ui/autoComplete/purchaseOrder/purchaseOrders-autoComplete";
import { useUserStore } from "@/store/userStore";
import { getUserCodeByUser } from "@/utils/checkData";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  PartnersGet200ResponseRowsInnerTypeEnum,
  PurchaseOrdersGet200ResponseRowsInner,
  ScmApiPurchaseOrderItemsPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
  DateInput: DateInput,
});

interface PurchaseOrderCreateFormProps {
  purchaseOrderId?: number;
  purchaseOrder?: PurchaseOrdersGet200ResponseRowsInner;
}

export const PurchaseOrderItemsCreateForm = (props: PurchaseOrderCreateFormProps) => {
  const { purchaseOrderId, purchaseOrder } = props;
  const { closeModal } = useModal();
  const userCode = getUserCodeByUser();
  const { userInfo } = useUserStore();
  const partnerType = userInfo?.partner?.type;

  const { t } = useTranslation();
  const [id, setId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutate: createMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPostRequest) =>
      mutatePurchaseOrderItems
        .create(params)
        .mutationFn(params as ScmApiPurchaseOrderItemsPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["purchaseOrderItems"]);
        closeModal(form.values);
        customAlert(
          t("message.createSuccess", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          t("common.createSuccess"),
          "green"
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(
              t,
              "purchaseOrderItems.purchaseOrderItems",
              partnerType
            ),
          }),
          t("common.createFail", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          "red"
        );
      },
    }
  );

  const { data: purchaseOrderItem } = useQuery({
    ...purchaseOrders.detail({
      purchaseOrderId: purchaseOrderId ?? Number(id) ?? 0,
      populate: ["location", "user", "partner", "purchaseOrderItems"],
    }),
    enabled: !!purchaseOrderId || !!id,
  });

  const getInitialValues = (
    purchaseOrder: PurchaseOrdersGet200ResponseRowsInner | undefined,
    purchaseOrderItem: AxiosResponse<PurchaseOrdersGet200ResponseRowsInner, any> | undefined
  ) => {
    const orderDateString = purchaseOrder?.orderDate ?? purchaseOrderItem?.data?.orderDate;
    const deliveryDeadlineString =
      purchaseOrder?.deliveryDeadline ?? purchaseOrderItem?.data?.deliveryDeadline;

    const isValidDate = (value: any) => {
      const date = new Date(value);
      return !isNaN(date.getTime());
    };

    return {
      purchaseOrderId: purchaseOrderId
        ? purchaseOrderId
        : purchaseOrder
          ? purchaseOrder.id
          : purchaseOrderItem?.data?.id,
      partnerCode: purchaseOrderItem?.data?.partnerCode ?? purchaseOrder?.partnerCode ?? undefined,
      itemCode: undefined,
      quantity: undefined,
      orderDate:
        isValidDate(orderDateString) && orderDateString !== undefined
          ? new Date(orderDateString)
          : undefined,
      deliveryDeadline:
        deliveryDeadlineString !== undefined && isValidDate(deliveryDeadlineString)
          ? new Date(deliveryDeadlineString)
          : undefined,
      userCode: purchaseOrderItem?.data?.userCode ?? purchaseOrder?.userCode ?? undefined,
      sourceLocationCode:
        purchaseOrderItem?.data?.sourceLocationCode ??
        purchaseOrder?.sourceLocationCode ??
        undefined,
    };
  };

  const form = useForm({
    initialValues: getInitialValues(purchaseOrder, purchaseOrderItem),
  });

  useEffect(() => {
    if (purchaseOrderItem) {
      form.setValues(getInitialValues(purchaseOrder, purchaseOrderItem));
    }
  }, [purchaseOrderItem]);

  const onSubmit = async () => {
    try {
      createMutate({
        purchaseOrderItemsGetRequest: {
          purchaseOrderId: purchaseOrderId
            ? purchaseOrderId
            : purchaseOrder
              ? purchaseOrder.id
              : purchaseOrderItem
                ? purchaseOrderItem?.data?.id
                : form.values.purchaseOrderId!,
          partnerCode: purchaseOrder?.partnerCode ?? form.values.partnerCode!,
          direction: "INBOUND",
          itemCode: form.values.itemCode!,
          quantity: form.values.quantity!,
          orderDate: purchaseOrder?.orderDate?.toString()! ?? form.values.orderDate!,
          deliveryDeadline:
            purchaseOrder?.deliveryDeadline?.toString()! ?? form.values.deliveryDeadline!,
          userCode: purchaseOrder?.userCode ?? form.values.userCode!,
          sourceLocationCode: purchaseOrder?.sourceLocationCode ?? form.values.sourceLocationCode!,
        },
      });
    } catch (e) {
      customAlert(
        handleErrorResponse({
          error: e,
          errorContext: translateWithPartnerType(
            t,
            "purchaseOrderItems.purchaseOrderItems",
            partnerType
          ),
        }),
        t("common.createFail", {
          item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
        }),
        "red"
      );
    }
  };

  return (
    <>
      <Create.Wrapper>
        {purchaseOrderId || purchaseOrder ? null : (
          <PurchaseOrderAutoComplete
            direction="INBOUND"
            label={translateWithPartnerType(t, "purchaseOrder.code", partnerType)}
            placeholder={t("placeholder.select", {
              item: translateWithPartnerType(t, "purchaseOrder.code", partnerType),
            })}
            withAsterisk
            {...form.getInputProps("purchaseOrderId")}
            clearable
            onChange={(e) => setId(e)}
          />
        )}
        <PartnerAutoComplete
          label={t("partner.partner")}
          placeholder={t("placeholder.select", { item: t("partner.partner") })}
          defaultValue={purchaseOrder?.partnerCode}
          {...form.getInputProps("partnerCode")}
          partnerType={[
            partnerType ?? PartnersGet200ResponseRowsInnerTypeEnum.SALES,
            PartnersGet200ResponseRowsInnerTypeEnum.BI_DIRECTION,
          ]}
          withAsterisk
        />
        <ItemAutoComplete
          withAsterisk
          label={t("item.code")}
          getItemType={["PRODUCT", "SEMI_PRODUCT"]}
          placeholder={t("placeholder.select", { item: t("item.code") })}
          {...form.getInputProps("itemCode")}
        />
        <Create.Input
          withAsterisk
          label={t("moldCavity.quantity")}
          placeholder={t("placeholder.input", { item: t("moldCavity.quantity") })}
          {...form.getInputProps("quantity")}
        />
        <LocationAutoComplete
          label={translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
          })}
          defaultValue={purchaseOrder?.sourceLocationCode}
          {...form.getInputProps("sourceLocationCode")}
        />
        <Create.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType)}
          valueFormat="YYYY-MM-DD"
          defaultValue={new Date()} // 오늘 날짜를 기본값으로 설정
          locale="ko"
          firstDayOfWeek={0}
          {...form.getInputProps("orderDate")}
        />
        <Create.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          withAsterisk
          label={t("order.deliveryDate")}
          minDate={form.values.orderDate ? new Date(form.values.orderDate) : undefined}
          valueFormat="YYYY-MM-DD"
          locale="ko"
          firstDayOfWeek={0}
          {...form.getInputProps("deliveryDeadline")}
        />
        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Create.Button>
          <Create.Button
            disabled={validateFormValues({
              pageType: "purchaseOrderItems",
              values: form.values,
            })}
            onClick={onSubmit}
          >
            {t("common.create", {
              item: translateWithPartnerType(
                t,
                "purchaseOrderItems.purchaseOrderItems",
                partnerType
              ),
            })}
          </Create.Button>
        </Create.ButtonBox>
      </Create.Wrapper>
    </>
  );
};
